import { supabase } from '@/lib/supabase';
import type {
  EmployeeAssignmentsInsert,
  EmployeeAssignmentsRow,
  EmployeeAssignmentsUpdate,
  EmployeeDirectoryRow,
  EmployeeProfileInsert,
  EmployeeProfileRow,
  EmployeeProfileUpdate,
} from '@/types/database.types';

/**
 * Directorio del equipo — vista agregada con perfil + asignaciones vigentes.
 * RLS gateada por employees.view.
 *
 * La view `core.v_employee_directory` NO está en `Database['core']['Views']`
 * a propósito: hand-written types tiene varios objetos sin tipar
 * (background_jobs, prospect_documents, ...) y poblar Views con la única
 * view que conocemos hace que from() pierda la flexibilidad de aceptar
 * relations no tipados. Cast a `any` puntual.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const directoryTable = () => (supabase as any).from('v_employee_directory');

export async function listDirectory(): Promise<EmployeeDirectoryRow[]> {
  const { data, error } = await directoryTable()
    .select('*')
    .order('display_name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as EmployeeDirectoryRow[];
}

export async function getDirectoryEntry(
  userId: string,
): Promise<EmployeeDirectoryRow | null> {
  const { data, error } = await directoryTable()
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as EmployeeDirectoryRow | null) ?? null;
}

/**
 * Perfil propio del usuario logueado. RLS de employee_profile permite SELECT
 * por employees.view, así que cualquier empleado puede leer la suya.
 */
export async function getMyProfile(
  userId: string,
): Promise<EmployeeProfileRow | null> {
  const { data, error } = await supabase
    .from('employee_profile')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function getProfile(
  userId: string,
): Promise<EmployeeProfileRow | null> {
  const { data, error } = await supabase
    .from('employee_profile')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function upsertProfile(
  input: EmployeeProfileInsert,
): Promise<EmployeeProfileRow> {
  const { data, error } = await supabase
    .from('employee_profile')
    .upsert(input)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function updateProfile(
  userId: string,
  patch: EmployeeProfileUpdate,
): Promise<EmployeeProfileRow> {
  const { data, error } = await supabase
    .from('employee_profile')
    .update(patch)
    .eq('user_id', userId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// ---------- Asignaciones ----------

export async function listAssignments(
  userId: string,
): Promise<EmployeeAssignmentsRow[]> {
  const { data, error } = await supabase
    .from('employee_assignments')
    .select('*')
    .eq('user_id', userId)
    .order('is_primary', { ascending: false })
    .order('started_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function createAssignment(
  input: EmployeeAssignmentsInsert,
): Promise<EmployeeAssignmentsRow> {
  const { data, error } = await supabase
    .from('employee_assignments')
    .insert(input)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function updateAssignment(
  id: string,
  patch: EmployeeAssignmentsUpdate,
): Promise<EmployeeAssignmentsRow> {
  const { data, error } = await supabase
    .from('employee_assignments')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Cierra una asignación vigente (soft): pone ended_at en hoy.
 * NO borra el row para mantener historial.
 */
export async function closeAssignment(
  id: string,
  endedAt: string = new Date().toISOString().slice(0, 10),
): Promise<EmployeeAssignmentsRow> {
  return updateAssignment(id, { ended_at: endedAt, is_primary: false });
}
